import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Header from '../../components/ui/Header';
import DashboardHeader from './components/DashboardHeader';
import UpcomingOccasionsCard from './components/UpcomingOccasionsCard';
import RelationshipProfilesCard from './components/RelationshipProfilesCard';
import GiftingAnalyticsCard from './components/GiftingAnalyticsCard';
import PersonalizedRecommendationsCard from './components/PersonalizedRecommendationsCard';
import QuickActionsCard from './components/QuickActionsCard';
import EmailVerification from '../../components/auth/EmailVerification';

// Temporary error logging component
class TemporaryErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Dashboard Error Caught:', error);
    console.error('Error Info:', errorInfo);
    this.setState({ error, errorInfo });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="bg-white p-8 rounded-lg shadow-lg max-w-2xl">
            <h1 className="text-2xl font-bold text-red-600 mb-4">Dashboard Error</h1>
            <p className="text-gray-700 mb-4">
              Error: {this.state.error?.message}
            </p>
            <details className="bg-gray-100 p-4 rounded">
              <summary className="font-semibold cursor-pointer">Error Details</summary>
              <pre className="mt-2 text-sm overflow-auto">
                {this.state.error?.stack}
              </pre>
            </details>
            <button
              onClick={() => window.location.reload()}
              className="mt-4 bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
            >
              Reload Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

const ProfileDashboard = () => {
  const { user, loading, emailVerificationRequired, verificationEmail } = useAuth();
  const navigate = useNavigate();
  
  // Debug logging
  console.log('ProfileDashboard - user:', user);
  console.log('ProfileDashboard - loading:', loading);
  console.log('ProfileDashboard - emailVerificationRequired:', emailVerificationRequired);

  // Dashboard data state
  const [dashboardStats, setDashboardStats] = useState([
    { icon: 'Gift', value: '0', label: 'Gifts Given' },
    { icon: 'Heart', value: '0', label: 'Wishlists' },
    { icon: 'Users', value: '0', label: 'Relationships' }
  ]);

  const [upcomingOccasions, setUpcomingOccasions] = useState([]);
  const [relationshipProfiles, setRelationshipProfiles] = useState([]);
  const [giftingAnalytics, setGiftingAnalytics] = useState({
    totalSpent: 0,
    giftsGiven: 0,
    successRate: 0,
    avgRating: 0,
    achievements: []
  });
  const [personalizedRecommendations, setPersonalizedRecommendations] = useState([]);

  useEffect(() => {
    if (user.id && !emailVerificationRequired) {
      loadDashboardData();
    }
  }, [user.id, emailVerificationRequired]);

  const loadDashboardData = async () => {
    try {
      // TODO: Replace with actual API calls
      console.log('Loading dashboard data for user:', user.id);

      // Mock data for now - will be replaced with real API calls
      setDashboardStats([
        { icon: 'Gift', value: '47', label: 'Gifts Given' },
        { icon: 'Heart', value: '12', label: 'Wishlists' },
        { icon: 'Users', value: '23', label: 'Relationships' }
      ]);

      setUpcomingOccasions([
        {
          id: 1,
          title: "Mom's Birthday",
          recipient: 'Linda Johnson',
          date: '2024-12-29',
          daysLeft: 3,
          type: 'birthday'
        }
      ]);

      setRelationshipProfiles([
        {
          id: 1,
          name: 'Michael Chen',
          relationship: 'Friend',
          successRate: 95,
          giftsGiven: 8,
          lastGift: 'Nov 2024',
          interests: ['Technology', 'Gaming', 'Coffee']
        }
      ]);

      setGiftingAnalytics({
        totalSpent: 2450,
        giftsGiven: 47,
        successRate: 94,
        avgRating: 4.8,
        achievements: [
          'Gift Guru - 50+ gifts given',
          'Perfect Match - 90% success rate',
          'Early Bird - Always on time'
        ]
      });

      setPersonalizedRecommendations([
        {
          id: 1,
          title: 'Smart Home Security Camera',
          price: 199.99,
          match: 92,
          recipient: 'Michael Chen',
          image: '/api/placeholder/300/300'
        }
      ]);

    } catch (error) {
      console.error('Error loading dashboard data:', error);
    }
  };

  const handleViewAllOccasions = () => {
    console.log('Viewing all occasions');
    navigate('/occasions');
  };

  const handleAddNewOccasion = () => {
    console.log('Adding new occasion');
    navigate('/occasions/new');
  };

  const handleSaveRecommendation = (recommendation) => {
    console.log('Saving recommendation:', recommendation);
    // TODO: Implement save recommendation functionality
  };

  const handleViewAllRecommendations = () => {
    console.log('Viewing all recommendations');
    // TODO: Navigate to recommendations page
  };

  const handleQuickAction = (actionId) => {
    switch (actionId) {
      case 'find-gift':
        navigate('/gift-finder');
        break;
      case 'create-wishlist':
        navigate('/wishlists');
        break;
      case 'add-occasion':
        navigate('/occasion-creation');
        break;
      case 'gift-concierge':
        navigate('/gift-concierge');
        break;
      case 'settings':
        navigate('/profile-settings');
        break;
      case 'help':
        console.log('Opening help...');
        break;
      case 'export':
        console.log('Exporting data...');
        break;
      default:
        break;
    }
  };

  const handleViewAllRelationships = () => {
    console.log('Viewing all relationships');
    navigate('/relationships');
    // TODO: Implement relationships page
  };

  const handleAddNewRelationship = () => {
    console.log('Adding new relationship');
    navigate('/relationships/new');
    // TODO: Implement add relationship page
  };

  // Show loading state while authentication is being initialized
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-accent-600">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  // Handle authentication redirect
  useEffect(() => {
    console.log('ProfileDashboard - Authentication check:', { loading, userId: user?.id, shouldRedirect: !loading && !user?.id });
    if (!loading && !user?.id) {
      console.log('ProfileDashboard - Redirecting to login');
      navigate('/login');
    }
  }, [loading, user?.id, navigate]);

  // Don't render if not authenticated
  if (!loading && !user?.id) {
    return null;
  }


  return (
    <TemporaryErrorBoundary>
      <div className="min-h-screen bg-background">
        <Header />

        <main className="pt-20 pb-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

            {/* Dashboard Header */}
            <DashboardHeader user={user} stats={dashboardStats} />

            {/* Email Verification Alert */}
            {emailVerificationRequired && (
              <div className="mb-8">
                <EmailVerification
                  email={verificationEmail || user.email}
                  onVerificationSuccess={() => window.location.reload()}
                  onBack={() => navigate('/login')}
                />
              </div>
            )}

            {/* Main Dashboard Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6 mb-8">
              {/* Upcoming Occasions */}
              <div className="xl:col-span-1">
                <UpcomingOccasionsCard
                  occasions={upcomingOccasions}
                  onViewAll={handleViewAllOccasions}
                  onAddNew={handleAddNewOccasion}
                />
              </div>

              {/* Quick Actions */}
              <div className="xl:col-span-1">
                <QuickActionsCard onAction={handleQuickAction} />
              </div>

              {/* Personalized Recommendations */}
              <div className="xl:col-span-1">
                <PersonalizedRecommendationsCard
                  recommendations={personalizedRecommendations}
                  onSave={handleSaveRecommendation}
                  onViewAll={handleViewAllRecommendations}
                />
              </div>
            </div>

            {/* Secondary Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
              {/* Relationship Profiles */}
              <RelationshipProfilesCard
                relationships={relationshipProfiles}
                onViewAll={handleViewAllRelationships}
                onAddNew={handleAddNewRelationship}
              />

              {/* Gifting Analytics */}
              <GiftingAnalyticsCard analytics={giftingAnalytics} />
            </div>
          </div>
        </main>
      </div>
    </TemporaryErrorBoundary>
  );
};

export default ProfileDashboard;